import { getCurrentOrgID } from './organization';
import ErrorHandler from '../utils/ErrorHandler';
import { supabase, queryClient } from '../data/client';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { isTransientError } from '@/utils/requestQueue';

const DEFAULT_DASHBOARD_SECTIONS = {
    organizationSetup: { visible: true, order: -1 },
    recentlyVisited: { visible: true, order: 0 },
    mySmallGroups: { visible: true, order: 1 },
    tasksNeedingAssigned: { visible: true, order: 2 },
    myUpcomingTasks: { visible: true, order: 3 },
    upcomingEvents: { visible: true, order: 4 },
    myTraining: { visible: true, order: 5 },
    myBoardsFeed: { visible: true, order: 6 },
    // §25.9 — permission-scoped active care. Defaults merge into saved
    // layouts via mergeDashboardSections; saved keys are never clobbered.
    activeCare: { visible: true, order: 7 },
};

const saveUserSettings = async (settings) => {
    const { data: { user } } = await supabase.auth.getUser()

    if (!user) throw Error("Not authenticated");

    const {error, data} = await supabase.from('RegisteredUsers').update({...settings, theme: settings.theme ? settings.theme.toUpperCase() : 'DEFAULT'}).eq('id', user.id);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "saveUserSettings", input: {...settings, userID:user.id}}, `Something went wrong saving your settings`);
        throw Error();
    }

    return data;
}

const getDevices = async () => {
    const { data: { user } } = await supabase.auth.getUser()

    if (!user) throw Error("Not authenticated");

    const { data: devices, error } = await supabase.from("Devices").select().eq("userID", user.id);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "getDevices", input: user.id, error})
        throw Error()
    }

    return devices;
}

const createDevice = async (details) => {
    // Dedupe on expoPushToken, scoped to the SAME userID and REAL tokens only:
    // if this user already has a Devices row with this push token, UPDATE it in
    // place (keeping the existing row id) instead of inserting a duplicate.
    // Duplicate rows sharing a token make the backend send one push per row →
    // duplicate notifications on a single device.
    //
    // The scope is deliberate. An earlier fix deleted ALL rows sharing the
    // token, which wiped OTHER users' devices (many rows share the 'SIMULATOR'
    // fallback when push registration fails) and caused random logouts via the
    // device guard in _layout.jsx. So: never touch another user's row, and
    // never collapse 'SIMULATOR' rows — multiple devices legitimately share
    // that fallback.
    const isRealPushToken = !!details.expoPushToken
        && String(details.expoPushToken).toLowerCase() !== 'simulator';

    if (isRealPushToken) {
        const { data: existingDevices, error: lookupError } = await supabase
            .from("Devices")
            .select("id")
            .eq("userID", details.userID)
            .eq("expoPushToken", details.expoPushToken);

        if (lookupError) {
            // Dedupe is best-effort — report, then fall through to insert
            ErrorHandler(Error(lookupError.message), {query: "createDevice.lookupExisting", input: details, error: lookupError})
        } else if (existingDevices?.length) {
            // Pre-dedupe rows can leave multiple devices sharing one token —
            // keep one row, delete the extras, then update in place.
            const [existingDevice, ...duplicateDevices] = existingDevices;
            if (duplicateDevices.length) {
                await supabase.from("Devices").delete().in("id", duplicateDevices.map(d => d.id));
            }
            const {error: updateError, data: updatedDevice} = await supabase.from("Devices").update({...details}).eq("id", existingDevice.id).select().single();

            if(updateError){
                ErrorHandler(Error(updateError.message), {mutation: "createDevice", input: details, error: updateError}, `Something went wrong adding this device`)
                return null;
            }

            return updatedDevice;
        }
    }

    const {error: createError, data: newDevice} = await supabase.from("Devices").insert({...details}).select().single();

    if(createError){
        ErrorHandler(Error(createError.message), {mutation: "createDevice", input: details, error: createError}, `Something went wrong adding this device`)
        return null;
    }

    return newDevice;
}


const getDeviceByID = async (deviceID) => {
    const { data: device, error } = await supabase.from("Devices").select("id").eq("id", deviceID).maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {query: "getDeviceByID", input: deviceID, error})
        throw Error()
    }

    return device;
}

const deleteDevice = async (deviceID) => {
    const {error} = await supabase.from("Devices").delete().eq("id", deviceID);
    console.log("deleteDevice error", error);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteDevice", input: deviceID, error})
        throw Error()
    }
}

const getNotificationTypes = async () => {
    const { data: notificationTypes, error } = await supabase.from("NotificationTypes").select().order("module").order("name");

    if(error){
        ErrorHandler(Error(error.message), {mutation: "getNotificationTypes", error})
        throw Error()
    }

    return notificationTypes
}

const getDashboardSections = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    const orgID = await getCurrentOrgID();

    if (!user || !orgID) return DEFAULT_DASHBOARD_SECTIONS;

    const { data, error } = await supabase
        .from("RegisteredUsersOrganizations")
        .select("dashboardSections")
        .eq("userID", user.id)
        .eq("organizationID", orgID)
        .maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { query: "getDashboardSections", error });
        return DEFAULT_DASHBOARD_SECTIONS;
    }

    // Merge user's saved sections with defaults to ensure new sections are included
    const userSections = data?.dashboardSections || {};
    const mergedSections = { ...DEFAULT_DASHBOARD_SECTIONS };

    // Preserve user's visibility/order preferences for existing sections
    Object.keys(userSections).forEach(key => {
        if (DEFAULT_DASHBOARD_SECTIONS[key]) {
            mergedSections[key] = userSections[key];
        }
    });

    return mergedSections;
};

/**
 * Merges raw dashboardSections jsonb from the RPC with DEFAULT_DASHBOARD_SECTIONS.
 * Mirrors the merge logic in getDashboardSections.
 */
const mergeDashboardSections = (rawSections) => {
    const userSections = rawSections || {};
    const mergedSections = { ...DEFAULT_DASHBOARD_SECTIONS };
    Object.keys(userSections).forEach(key => {
        if (DEFAULT_DASHBOARD_SECTIONS[key]) {
            mergedSections[key] = userSections[key];
        }
    });
    return mergedSections;
};

/**
 * Fetches all dashboard data in a single RPC call (get_dashboard_data).
 * Consolidates 6 queries: upcomingTasks, upcomingTaskAssignments,
 * smallGroups, shortcuts, upcomingTrainingClasses, dashboardSections.
 * @param {string} personID - The logged-in user's person ID.
 * @returns {Promise<object>} - { upcomingTasks, upcomingTaskAssignments, smallGroups, shortcuts, upcomingTrainingClasses, sections }
 */
const getDashboardData = async (personID) => {
    const { data, error } = await supabase.rpc('get_dashboard_data', {
        p_person_id: personID,
        p_task_horizon_days: 7,
        p_assignment_horizon_days: 30,
    });

    if (error) {
        // The RPC raises 'dashboard access denied' when the JWT carries no org
        // claim or no membership — a stale/transitional session state, not an
        // app bug. Surface it to the caller but don't report it (CHURCH-APP-1EZ).
        if (!(error.code === 'P0001' && error.message === 'dashboard access denied') && !isTransientError(error)) {
            ErrorHandler(Error(error.message), { query: "getDashboardData", personID, error }, "Something went wrong fetching dashboard data");
        }
        throw Error(error.message);
    }

    // Reshape upcomingTaskAssignments to add VolunteerTeamAssigners (matches client map)
    const upcomingTaskAssignments = (data?.upcomingTaskAssignments || []).map(taskInstance => ({
        ...taskInstance,
        VolunteerTeam: taskInstance.VolunteerTeam ? {
            ...taskInstance.VolunteerTeam,
            VolunteerTeamAssigners: [{ personID }]
        } : null
    }));

    return {
        upcomingTasks: data?.upcomingTasks || [],
        upcomingTaskAssignments,
        smallGroups: data?.smallGroups || [],
        shortcuts: data?.shortcuts || [],
        upcomingTrainingClasses: data?.upcomingTrainingClasses || [],
        sections: mergeDashboardSections(data?.dashboardSections),
    };
};

const saveDashboardSections = async (sections) => {
    const { data: { user } } = await supabase.auth.getUser();
    const orgID = await getCurrentOrgID();

    if (!user || !orgID) throw Error("Not authenticated");

    // Use a SECURITY DEFINER RPC instead of a direct table update. The UPDATE
    // RLS policy on RegisteredUsersOrganizations only permits org admins, so a
    // direct .update() is silently blocked for non-admins (0 rows, no error),
    // which causes the optimistic update to revert on the next refetch.
    const { error } = await supabase
        .rpc("update_own_dashboard_sections", { p_org_id: orgID, p_sections: sections });

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "saveDashboardSections", input: sections, error }, "Something went wrong saving dashboard settings");
        throw Error();
    }

    queryClient.invalidateQueries({ queryKey: QueryKeys.dashboardSections.get() });
    queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
    return sections;
};

export {
    saveUserSettings,
    getDevices,
    getDeviceByID,
    createDevice,
    deleteDevice,
    getNotificationTypes,
    getDashboardSections,
    getDashboardData,
    saveDashboardSections,
    DEFAULT_DASHBOARD_SECTIONS
}